import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Grid,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { getFinancialDashboardData } from '../../requests/api/dashboard';
import { DEFAULT_API_ERROR_MESSAGE } from '../../utils/errors';
import { thousandSeparator } from '../../utils/helpers';
import { useYear } from '../../hooks/useYear';
import DashboardSummaryCard from './components/DashboardSummaryCard';
import FinancialChart from './components/chart';
import { FinancialDashboardData } from './types';
import useStyles from './styles';

const referenceColors = ['#E2E2E2', '#706E6F', '#2C82B3', '#5BC2D5', '#F2AC32'];

const FinancialDashboard: React.FC = () => {
  const classes = useStyles();
  const { selectedYear } = useYear();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const [
    financialDashboardData, setFinancialDashboardData,
  ] = useState<FinancialDashboardData | null>({
    totalPaid: 0,
    percentageVariation: 0,
    totalTuition: 0,
    totalTuitionPaid: 0,
    totalTuitionDebt: 0,
    feeQuantity: 0,
    totalFee: 0,
    totalFeePaid: 0,
    totalFeeDebt: 0,
    totalIVMedio: 0,
    totalEgresados: 0,
    totalEgresadosInt: 0,
    totalBianualI: 0,
    totalBianualII: 0,
    tuitionQuantity: 0,
    totalMath: 0,
    totalScience: 0,
    totalSocialStudies: 0,
    totalLanguage: 0,
  } as FinancialDashboardData);

  useEffect(() => {
    const fetchFinancialDashboardData = async () => {
      try {
        const response = await getFinancialDashboardData(selectedYear);
        setFinancialDashboardData(response.financialDashboardData);
      } catch (err) {
        const e = err as AxiosError;
        enqueueSnackbar(e.response?.data.message || DEFAULT_API_ERROR_MESSAGE, {
          variant: 'error',
        });
      }
    };

    fetchFinancialDashboardData();
  }, [selectedYear]);

  return (
    <Box className={classes.dashboardSectionRoot}>
      <Box className={classes.totalCard}>
        <Box className={classes.titleContainer}>
          <Typography className={classes.totalLabel}>
            Ingresos totales
          </Typography>
          <ArrowForwardIosIcon onClick={() => history.push('/dashboard/financiero/ingresos-totales')} />
        </Box>
        <Box display="flex" alignItems="baseline" mt={1}>
          <Typography className={classes.totalAmount}>
            ${thousandSeparator(financialDashboardData?.totalPaid || 0, '.')}
          </Typography>
          {selectedYear > 2026 && (
            <Box
              className={`${classes.percentage} ${financialDashboardData?.percentageVariation && financialDashboardData?.percentageVariation > 0 ? classes.positive : classes.negative}`}
            >
              {financialDashboardData?.percentageVariation
                && financialDashboardData?.percentageVariation >= 0
                ? <TrendingUpIcon />
                : <TrendingDownIcon />}
              <Typography className={classes.percentageText}>
                {financialDashboardData?.percentageVariation}%
              </Typography>
            </Box>
          )}
        </Box>
        <Typography className={classes.totalDescription}>
          Total acumulado
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardSummaryCard
            title="Ingresos por asignatura"
            onNavigate={() => history.push('/dashboard/financiero/ingresos-por-asignatura')}
            items={[
              { label: 'Matemáticas', value: `$${thousandSeparator(financialDashboardData?.totalMath || 0, '.')}`, color: referenceColors[0] },
              { label: 'Lenguaje', value: `$${thousandSeparator(financialDashboardData?.totalLanguage || 0, '.')}`, color: referenceColors[1] },
              { label: 'Ciencias', value: `$${thousandSeparator(financialDashboardData?.totalScience || 0, '.')}`, color: referenceColors[2] },
              { label: 'Sociales', value: `$${thousandSeparator(financialDashboardData?.totalSocialStudies || 0, '.')}`, color: referenceColors[3] },
            ]}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <DashboardSummaryCard
            title="Ingresos por nivel"
            onNavigate={() => history.push('/dashboard/financiero/ingresos-por-nivel')}
            items={[
              { label: 'IV Medio', value: `$${thousandSeparator(financialDashboardData?.totalIVMedio || 0, '.')}`, color: referenceColors[0] },
              { label: 'Egresados', value: `$${thousandSeparator(financialDashboardData?.totalEgresados || 0, '.')}`, color: referenceColors[1] },
              { label: 'Egresados (Int.)', value: `$${thousandSeparator(financialDashboardData?.totalEgresadosInt || 0, '.')}`, color: referenceColors[2] },
              { label: 'Bianual I', value: `$${thousandSeparator(financialDashboardData?.totalBianualI || 0, '.')}`, color: referenceColors[3] },
              { label: 'Bianual II', value: `$${thousandSeparator(financialDashboardData?.totalBianualII || 0, '.')}`, color: referenceColors[4] },
            ]}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <DashboardSummaryCard
            title="Ingresos por matrículas"
            detailText={`${financialDashboardData?.tuitionQuantity} matrícula${financialDashboardData?.tuitionQuantity && financialDashboardData?.tuitionQuantity > 1 ? 's' : ''}`}
            onNavigate={() => history.push('/dashboard/financiero/ingresos-por-matriculas')}
            items={[
              { label: 'Total', value: `$${thousandSeparator(financialDashboardData?.totalTuition || 0, '.')}`, color: referenceColors[0] },
              { label: 'Pagados', value: `$${thousandSeparator(financialDashboardData?.totalTuitionPaid || 0, '.')}`, color: referenceColors[1] },
              { label: 'Por pagar', value: `$${thousandSeparator(financialDashboardData?.totalTuitionDebt || 0, '.')}`, color: referenceColors[2] },
            ]}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <DashboardSummaryCard
            title="Ingresos por aranceles"
            detailText={`${financialDashboardData?.feeQuantity} programa${financialDashboardData?.feeQuantity && financialDashboardData?.feeQuantity > 1 ? 's' : ''}`}
            onNavigate={() => history.push('/dashboard/financiero/ingresos-por-aranceles')}
            items={[
              { label: 'Total', value: `$${thousandSeparator(financialDashboardData?.totalFee || 0, '.')}`, color: referenceColors[0] },
              { label: 'Pagados', value: `$${thousandSeparator(financialDashboardData?.totalFeePaid || 0, '.')}`, color: referenceColors[1] },
              { label: 'Por pagar', value: `$${thousandSeparator(financialDashboardData?.totalFeeDebt || 0, '.')}`, color: referenceColors[2] },
            ]}
          />
        </Grid>
      </Grid>

      <Box className={classes.chartCard}>
        <Box className={classes.chartHeader}>
          <Typography variant="subtitle2" className={classes.totalLabel}>
            Total de ingresos acumulados por año
          </Typography>
        </Box>

        <Box>
          <FinancialChart
            monthlyPaid={financialDashboardData?.monthlyPaid || []}
            monthlyPaidLastYear={financialDashboardData?.monthlyPaidLastYear || []}
            currentYearLabel={`${selectedYear}`}
            lastYearLabel={`${Number(selectedYear) - 1}`}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default FinancialDashboard;
